import { ProjectService } from '../projects/project.service';
import { NotificationService } from './../core/notification/notification.service';

import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';

import packageJson from './../../../package.json';
import { IonSearchbar, Platform } from '@ionic/angular';
import { ReplaySubject, Subscription } from 'rxjs';
import { AuthService } from '../auth/auth.service';

import { DbService } from '../core/db/db.service';
import { LoggerService } from '../core/logger/logger.service';
import { Address } from '../core/shared-models/address.model';
import { SyncInfoInterface } from '../core/sync/sync-info.interface';
import { SyncService } from '../core/sync/sync.service';
import { UtilsService } from '../core/utils/utils.service';
import { Project, ProjectStatus } from '../projects/project.model';
import { SettingsService } from '../settings/settings.service';

import { DashboardService } from './dashboard.service';
import { DpStatusMessage } from '../core/shared-models/mp-interfaces.interface';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Components } from '@ionic/core';
import IonTabs = Components.IonTabs;
import { takeUntil } from 'rxjs/operators';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { StorageService } from '../core/storage/storage.service';
import { TasksService } from '../tasks/tasks.service';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.page.html',
    styleUrls: ['./dashboard.page.scss'],
})
export class DashboardPage implements OnInit, OnDestroy {
    @ViewChild('projectSearchBarInput') projectSearchBarInput: IonSearchbar;
    @ViewChild('supportProjectSearchbar') supportProjectSearchbar: IonSearchbar;
    @ViewChild('virtualProjectList', { static: false }) virtualProjectList: CdkVirtualScrollViewport;
    @ViewChild('virtualProjectFolderList', { static: false }) virtualProjectFolderList: CdkVirtualScrollViewport;
    @ViewChild('tabs', { static: false }) tabs: IonTabs;
    projectSearchBarVisible = false;
    currentFolderName = null; // set if the user currently views a folder
    projectsOfCurrentFolder: Project[]; // holds all projects in current folder

    updateStatusMessageTimestamp = 0;
    dismissedStatusMessageTimestamp = 0;

    notificationCount = 0;
    openTasksCount = 0;

    // status message (maintenance, etc.)
    public statusMessage: DpStatusMessage = {
        enabled: false,
        message: '',
        title: '',
        type: 'warning',
        currentSoftwareVersion: '',
        warningSoftwareMinVersion: '',
    };

    // helper variables
    isDokuPitSupportAccount = false;
    dokuPitSupportSpoofingUsername = null;
    showStatistics = false;
    syncInfo: SyncInfoInterface = null;
    showVersionWarning = false;
    showReloadMessage = false;
    updateInfoVisible = false;
    mobileImportantNotificationWarningVisible = false;
    possibleProjectStatuses = ProjectStatus;
    projectEditFolderModeActive = false; // true if we are currently editing project folders
    changedProjectFolders: Project[] = []; // used to keep track of projects where folders changed (used for saving)
    availableProjectFolders: string[] = [];
    virtualScrollItemHeight = 60; // approx item height for virtual scroll (mainly for mobile devices due to different sizes)

    get shouldShowStatusMessageCardAtTop(): boolean {
        return (
            this.statusMessage?.enabled &&
            (this.dismissedStatusMessageTimestamp === 0 || this.updateStatusMessageTimestamp > this.dismissedStatusMessageTimestamp)
        );
    }

    syncInfoSubscription: Subscription;
    projectListSubscription: Subscription;
    projectAvailableFolderNamesSubscription: Subscription;
    projectListLoadingSubscription: Subscription;
    statusMessageSubscription: Subscription;
    notificationListSub: Subscription;
    taskListSub: Subscription;

    private destroyed$: ReplaySubject<boolean>;

    constructor(
        private projSrv: ProjectService,
        private storage: StorageService,
        private syncSrv: SyncService,
        private db: DbService,
        private authSrv: AuthService,
        private dashboardSrv: DashboardService,
        private logger: LoggerService,
        public settingsSrv: SettingsService,
        public utils: UtilsService,
        public translateService: TranslateService,
        private notifySrv: NotificationService,
        private platform: Platform,
        private router: Router,
        private tasksSrv: TasksService
    ) {
        this.destroyed$ = new ReplaySubject<boolean>(1);
    }

    async ngOnInit() {
        // set tab to dashboard
        if (this.utils.mobileViewActive && this.tabs != null) {
            this.tabs.select('dashboard');
        }

        if (this.projectSearchBarInput) {
            this.projectSearchBarInput.value = '';
        }

        this.currentFolderName = null;
        this.projectsOfCurrentFolder = [];

        this.syncInfoSubscription = this.syncSrv.syncInfo$.pipe(takeUntil(this.destroyed$)).subscribe((syncInfo) => {
            this.syncInfo = syncInfo;
        });

        this.statusMessageSubscription = this.dashboardSrv.statusMessage$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((statusMessage: DpStatusMessage) => {
                this.statusMessage = statusMessage;
                this.updateStatusMessageTimestamp = Date.now();
                this.checkSoftwareVersion();
            });

        this.notificationListSub = this.dashboardSrv.notifications$.pipe(takeUntil(this.destroyed$)).subscribe((notificationList) => {
            this.notificationCount = Array.isArray(notificationList) ? notificationList.length : 0;
        });

        this.taskListSub = this.tasksSrv.currentTaskList$.pipe(takeUntil(this.destroyed$)).subscribe((currentTaskList) => {
            this.openTasksCount = currentTaskList.filter((x) => x.workingStatus !== 'done').length;
        });

        this.isDokuPitSupportAccount = this.authSrv.authInfo.userData?.dokupitSupportAccount;
        this.dokuPitSupportSpoofingUsername = this.authSrv.authInfo.userData?.dokupitSupportSpoofUsername;
        this.showStatistics = this.settingsSrv.getUserRights().canSeeDashboardTodayStatistics;

        this.dismissedStatusMessageTimestamp = (await this.storage.get('dp_dismissed_status_message_timestamp')) || 0;

        await this.showUpdateInfo();

        this.mobileImportantNotificationWarningVisible = false;
        if (this.statusMessage?.enabled || this.updateInfoVisible || this.showVersionWarning || this.showReloadMessage) {
            this.mobileImportantNotificationWarningVisible = true;
        }
    }

    async ngOnDestroy() {
        // trigger unsub of all subscriptions
        this.destroyed$.next(true);
    }

    /**
     * Triggered when the user wants to dismiss an reminder
     */
    /*onDismissReminder(item: Job | Project, reminder: Reminder) {
		this.dashboardSrv.dismissReminder(item, reminder);
	}*/

    /**
     * If mobile tab view changed
     * @param event the tab event
     */
    tabViewChanged(event: any) {
        this.dashboardSrv.tabViewChanged.emit(event.tab);
    }

    /**
     * Shows update info if an update has been installed
     */
    private async showUpdateInfo() {
        // check if we need to show the update-installed info
        const updateInfo = await this.storage.get('dp_update_info_version');
        this.updateInfoVisible = updateInfo != null && updateInfo != packageJson.version;
        await this.storage.set('dp_update_info_version', packageJson.version);
    }

    /**
     * Checks if we need to show a warning for an old app version
     * @private
     */
    private checkSoftwareVersion() {
        if (this.statusMessage.warningSoftwareMinVersion != null && this.statusMessage.warningSoftwareMinVersion.trim() != '') {
            let v1: any = packageJson.version;
            let v2: any = this.statusMessage.warningSoftwareMinVersion;

            this.showVersionWarning = this.utils.compareVersionStrings(v1, v2);
        } else {
            this.showVersionWarning = false;
        }

        if (this.statusMessage.currentSoftwareVersion != null && this.statusMessage.currentSoftwareVersion.trim() != '') {
            this.showReloadMessage =
                this.utils.compareVersionStrings(packageJson.version, this.statusMessage.currentSoftwareVersion) && !this.utils.isNative;
        }
    }

    /**
     * Build project address
     * @param address
     */
    buildProjectAddressString(address: Address) {
        return this.utils.buildProjectAddressString(address);
    }

    reloadWebsite() {
        window.location.reload();
    }

    trackByItemId(index: number, item: any): number {
        if (item.entryType == 'project') {
            return item.project._id;
        }

        return index;
    }

    async dismissStatusMessageCard() {
        this.dismissedStatusMessageTimestamp = this.updateStatusMessageTimestamp;
        await this.storage.set('dp_dismissed_status_message_timestamp', this.dismissedStatusMessageTimestamp);
    }
}
